import React from 'react';
import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Button } from '../components/Button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/Breadcrumb';
import { facilitators } from '../data/facilitators';
import { destinations } from '../data/destinations';
import { MapPin, Users, CheckCircle2, DollarSign, ArrowRight } from 'lucide-react';
export function FacilitatorDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const facilitator = facilitators.find(f => f.slug === slug);
  if (!facilitator) {
    return <Navigate to="/not-found" replace data-id="element-549" />;
  }
  const parentDestination = destinations.find(d => d.country === facilitator.country);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://affordablemedicaltourism.com/'
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Facilitators',
      item: 'https://affordablemedicaltourism.com/facilitators'
    }, {
      '@type': 'ListItem',
      position: 3,
      name: facilitator.name,
      item: `https://affordablemedicaltourism.com/facilitators/${facilitator.slug}`
    }]
  };
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-550">
      <Helmet data-id="element-551">
        <title data-id="element-552">{facilitator.name} | Affordable Medical Tourism</title>
        <meta name="description" content={`Learn about ${facilitator.name}, a trusted medical tourism facilitator in ${facilitator.city}, ${facilitator.country}.`} data-id="element-553" />
        <link rel="canonical" href={`https://affordablemedicaltourism.com/facilitators/${facilitator.slug}`} data-id="element-554" />
        <script type="application/ld+json" data-id="element-555">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <AppHeader data-id="element-556" />

      <main className="flex-1" data-id="element-557">
        {/* Hero */}
        <div className="relative h-[40vh] min-h-[400px] w-full bg-slate-900" data-id="element-558">
          <img src={facilitator.image} alt={`${facilitator.name} in ${facilitator.city}`} className="absolute inset-0 w-full h-full object-cover opacity-40" data-id="element-559" />
          <div className="absolute inset-0 flex items-center" data-id="element-560">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full" data-id="element-561">
              <Breadcrumb className="mb-6" data-id="element-562">
                <BreadcrumbList data-id="element-563">
                  <BreadcrumbItem data-id="element-564">
                    <BreadcrumbLink href="/" className="text-slate-300 hover:text-white" data-id="element-565">
                      Home
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-slate-500" data-id="element-566" />
                  <BreadcrumbItem data-id="element-567">
                    <BreadcrumbLink href="/facilitators" className="text-slate-300 hover:text-white" data-id="element-568">
                      Facilitators
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-slate-500" data-id="element-569" />
                  <BreadcrumbItem data-id="element-570">
                    <BreadcrumbPage className="text-white font-medium" data-id="element-571">
                      {facilitator.name}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <div className="flex items-center gap-2 text-blue-200 mb-4" data-id="element-572">
                <MapPin className="w-5 h-5" data-id="element-573" />
                <span className="text-lg" data-id="element-574">
                  {facilitator.city}, {facilitator.country}
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-id="element-575">
                {facilitator.name}
              </h1>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-576">
          {/* Main */}
          <div className="lg:col-span-2 space-y-12" data-id="element-577">
            <section data-id="element-578">
              <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-579">
                About this Facilitator
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed" data-id="element-580">
                {facilitator.description}
              </p>
            </section>

            <section data-id="element-581">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-582">
                <Users className="w-6 h-6 text-blue-600" data-id="element-583" />
                Services Provided
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-584">
                {facilitator.services.map(service => <div key={service} className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-585">
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" data-id="element-586" />
                    <span className="font-medium text-slate-800" data-id="element-587">
                      {service}
                    </span>
                  </div>)}
              </div>
            </section>

            {parentDestination && <Link to={`/destinations/${parentDestination.slug}`} className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" data-id="element-588">
                Explore medical tourism in {facilitator.country}
                <ArrowRight className="w-4 h-4" data-id="element-589" />
              </Link>}
          </div>

          {/* Sidebar */}
          <div className="space-y-8" data-id="element-590">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100" data-id="element-591">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-592">
                Service Overview
              </h2>
              <dl className="space-y-6" data-id="element-593">
                <div data-id="element-594">
                  <dt className="text-sm text-slate-500 mb-1 flex items-center gap-2" data-id="element-595">
                    <DollarSign className="w-4 h-4" data-id="element-596" />
                    Illustrative Package Pricing
                  </dt>
                  <dd className="text-lg font-semibold text-slate-900" data-id="element-597">
                    {facilitator.totalPackagePrice}
                  </dd>
                </div>
              </dl>

              <div className="mt-8 pt-8 border-t border-slate-200" data-id="element-598">
                <Button size="lg" className="w-full" onClick={() => navigate('/contact')} data-id="element-599">
                  Plan your trip
                </Button>
                <p className="text-xs text-slate-500 text-center mt-3" data-id="element-600">
                  Our care team will coordinate directly with {facilitator.name}{' '}
                  to arrange your logistics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <AppFooter data-id="element-601" />
    </div>;
}